import { calculateHeat, getHeatIcon, HEAT_TIERS } from '../../lib/heatMeter';

/**
 * HeatMeter Component
 *
 * Displays a deal quality indicator based on value and interest.
 * Returns null for "normal" tier (no special indicator).
 *
 * @param {Object} raffle - The raffle object
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Whether to show the label text
 */
export default function HeatMeter({ raffle, size = 'md', showLabel = true }) {
  const tier = calculateHeat(raffle);

  // Don't render anything for normal tier
  if (tier.id === 'normal') {
    return null;
  }

  const icon = getHeatIcon(tier);

  // Size styles
  const sizeStyles = {
    sm: 'text-xs px-1.5 py-0.5 gap-1',
    md: 'text-sm px-2 py-1 gap-1.5',
    lg: 'text-base px-3 py-1.5 gap-2',
  };

  const iconSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <span
      className={`
        inline-flex items-center rounded-full
        font-medium whitespace-nowrap
        ${tier.bgColor} ${tier.borderColor} border
        ${sizeStyles[size] || sizeStyles.md}
      `}
    >
      <span className={iconSizes[size] || iconSizes.md} aria-hidden="true">
        {icon}
      </span>
      {showLabel && (
        <span className={tier.color}>
          {tier.label}
        </span>
      )}
    </span>
  );
}

/**
 * HeatMeterIcon
 *
 * Displays only the icon, useful for compact displays.
 */
export function HeatMeterIcon({ raffle, size = 'md' }) {
  const tier = calculateHeat(raffle);

  if (tier.id === 'normal') {
    return null;
  }

  const icon = getHeatIcon(tier);

  const sizeStyles = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <span
      className={`${sizeStyles[size]} ${tier.color}`}
      title={tier.label}
      aria-label={tier.label}
    >
      {icon}
    </span>
  );
}

/**
 * HeatMeterBadge
 *
 * A badge-style variant that sits in a corner or overlay position.
 */
export function HeatMeterBadge({ raffle }) {
  const tier = calculateHeat(raffle);

  if (tier.id === 'normal') {
    return null;
  }

  const icon = getHeatIcon(tier);

  return (
    <div
      className={`
        absolute top-2 left-2 z-10
        inline-flex items-center gap-1
        px-2 py-1 rounded-full
        text-xs font-semibold
        backdrop-blur-sm
        ${tier.bgColor} ${tier.borderColor} border
        shadow-lg
      `}
    >
      <span aria-hidden="true">{icon}</span>
      <span className={tier.color}>{tier.label}</span>
    </div>
  );
}
